import { env } from "../config/env";
import { createClient } from "./http";

const authApi = createClient(env.AUTH_BASE_URL);
const catalogApi = createClient(env.CATALOG_BASE_URL);
const warehouseApi = createClient(env.WAREHOUSE_BASE_URL);
const deliveryPartnerApi = createClient(env.DELIVERY_PARTNER_BASE_URL);

function unwrapData(res = {}) {
  return res?.data?.data || res?.data || res?.summary || res || {};
}

function toNumber(value) {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n : 0;
}

function normalizeUsersSummary(res = {}) {
  const data = unwrapData(res);

  return {
    totalUsers: toNumber(data.totalUsers ?? data.total_users ?? data.total ?? data.count),
    activeUsers: toNumber(data.activeUsers ?? data.active_users ?? data.active),
    inactiveUsers: toNumber(data.inactiveUsers ?? data.inactive_users ?? data.inactive),
    verifiedUsers: toNumber(data.verifiedUsers ?? data.verified_users ?? data.verified),
    profileCompletedUsers: toNumber(
      data.profileCompletedUsers ??
        data.profile_completed_users ??
        data.profileCompleted ??
        data.profile_completed
    ),
    raw: data,
  };
}

function normalizeEmployeesSummary(res = {}) {
  const data = unwrapData(res);

  return {
    totalEmployees: toNumber(data.totalEmployees ?? data.total_employees ?? data.total),
    activeEmployees: toNumber(data.activeEmployees ?? data.active_employees ?? data.active),
    inactiveEmployees: toNumber(data.inactiveEmployees ?? data.inactive_employees ?? data.inactive),
    pendingApprovals: toNumber(
      data.pendingApprovals ?? data.pending_approvals ?? data.pending
    ),
    approvedEmployees: toNumber(data.approvedEmployees ?? data.approved_employees ?? data.approved),
    rejectedEmployees: toNumber(data.rejectedEmployees ?? data.rejected_employees ?? data.rejected),
    notSubmitted: toNumber(data.notSubmitted ?? data.not_submitted ?? data.notsubmitted),
    raw: data,
  };
}

function normalizeDriversSummary(res = {}) {
  const data = unwrapData(res);

  return {
    totalDrivers: toNumber(data.totalDrivers ?? data.total_drivers ?? data.total),
    activeDrivers: toNumber(data.activeDrivers ?? data.active_drivers ?? data.active),
    inactiveDrivers: toNumber(data.inactiveDrivers ?? data.inactive_drivers ?? data.inactive),
    busyDrivers: toNumber(data.busyDrivers ?? data.busy_drivers ?? data.busy),
    offlineDrivers: toNumber(data.offlineDrivers ?? data.offline_drivers ?? data.offline),
    pendingApprovals: toNumber(data.pendingApprovals ?? data.pending_approvals ?? data.pending),
    approvedDrivers: toNumber(data.approvedDrivers ?? data.approved_drivers ?? data.approved),
    rejectedDrivers: toNumber(data.rejectedDrivers ?? data.rejected_drivers ?? data.rejected),
    raw: data,
  };
}

function normalizeStockHealthSummary(res = {}) {
  const data = unwrapData(res);

  return {
    lowStock: toNumber(data.lowStock ?? data.low_stock ?? data.low),
    expiringSoon: toNumber(data.expiringSoon ?? data.expiring_soon ?? data.expiring),
    outOfStock: toNumber(data.outOfStock ?? data.out_of_stock ?? data.out),
    raw: data,
  };
}

function normalizeProductsSummary(res = {}) {
  const data = unwrapData(res);

  return {
    totalProducts: toNumber(data.totalProducts ?? data.total_products ?? data.total),
    activeProducts: toNumber(data.activeProducts ?? data.active_products ?? data.active),
    inactiveProducts: toNumber(data.inactiveProducts ?? data.inactive_products ?? data.inactive),
    raw: data,
  };
}

function normalizePostersSummary(res = {}) {
  const data = unwrapData(res);

  return {
    totalPosters: toNumber(data.totalPosters ?? data.total_posters ?? data.total),
    activePosters: toNumber(data.activePosters ?? data.active_posters ?? data.active),
    inactivePosters: toNumber(data.inactivePosters ?? data.inactive_posters ?? data.inactive),
    raw: data,
  };
}

function normalizeBroadcastsSummary(res = {}) {
  const data = unwrapData(res);

  return {
    totalBroadcasts: toNumber(data.totalBroadcasts ?? data.total_broadcasts ?? data.total),
    activeBroadcasts: toNumber(data.activeBroadcasts ?? data.active_broadcasts ?? data.active),
    inactiveBroadcasts: toNumber(data.inactiveBroadcasts ?? data.inactive_broadcasts ?? data.inactive),
    currentlyLiveBroadcasts: toNumber(
      data.currentlyLiveBroadcasts ??
        data.currently_live_broadcasts ??
        data.liveBroadcasts ??
        data.live
    ),
    raw: data,
  };
}

export const dashboardSummaryService = {
  async getUsersSummary() {
    const res = await authApi.get("/api/user-auth/admin/users/summary");
    return normalizeUsersSummary(res);
  },

  async getEmployeesSummary(params = {}) {
    const res = await authApi.get("/api/admin/employees/summary", { params });
    return normalizeEmployeesSummary(res);
  },

  async getDeliveryPartnersSummary() {
    const [accountRes, presenceRes] = await Promise.all([
      authApi.get("/api/admin/delivery-partners/summary"),
      deliveryPartnerApi.get("/api/admin/delivery-partners/presence", {
        params: { limit: 5000 },
      }),
    ]);

    const account = normalizeDriversSummary(accountRes);
    const live = presenceRes?.summary || presenceRes?.data?.summary || {};

    return {
      ...account,
      activeDrivers: toNumber(live.activeDrivers ?? live.availableDrivers),
      availableDrivers: toNumber(live.availableDrivers ?? live.activeDrivers),
      busyDrivers: toNumber(live.busyDrivers),
      pausedDrivers: toNumber(live.pausedDrivers),
      offlineDrivers: toNumber(live.offlineDrivers),
      onlineDrivers: toNumber(live.onlineDrivers),
      eligibleDrivers: toNumber(live.eligibleDrivers),
      liveRaw: live,
    };
  },

  async getStockHealthSummary(params = {}) {
    const res = await catalogApi.get("/api/admin/inventory/stock-health-summary", {
      params,
    });
    return normalizeStockHealthSummary(res);
  },

  async getProductsSummary() {
    const res = await catalogApi.get("/api/admin/products/summary");
    return normalizeProductsSummary(res);
  },

  async getPostersSummary() {
    const res = await warehouseApi.get("/api/admin/posters/summary");
    return normalizePostersSummary(res);
  },

  async getBroadcastsSummary() {
    const res = await authApi.get("/api/admin/user-broadcasts/summary");
    return normalizeBroadcastsSummary(res);
  },
};